// src/StaticPages.tsx
import { useEffect } from 'react';
import './StaticPages.css';

export function AboutPage() {
  useEffect(() => { document.title = 'About · Shelfkin'; }, []);
  return (
    <div className="static-page">
      <div className="static-page-header">
        <h1 className="static-page-title">About</h1>
      </div>
      <div className="static-page-body">
        <h2>What is Shelfkin</h2>
        <p>
          Shelfkin is a platform to simplify the creation, uploading, and analysis of high quality Facebook Ad content for Amazon-focused authors.
          High performing ad styles changes over time and making them usually requires the use and learning of many tools.
          Shelfkin aims to make it easy for you to create these videos easily, while adhering to known good practices.  
        </p>

        <h2>Why Shelfkin was created</h2>
        <p>
          Marketing takes a lot of time for an author. They have difficulty:
          <ul>
            <li>finding the time for marketing</li>
            <li>knowing which ad styles work best</li>
            <li>learning the various tools to make high quality ads</li>
            <li>attributing and analyzing ads to know what truly works</li>
            <li>Facebook setup for success</li>
          </ul>
        </p>
        <p>
          Today, Shelfkin will help you quickly writes effective ads using proven ad templates.
          Soon, it will also create your Amazon attributions and upload to Facebook.
        </p>

        <h2>Who started Shelfkin</h2>
        <p>
          Shelfkin was created by Emma and Steven Shelford. Steven has 30 years of software development experience,
          has a PhD in Computer Science, and worked at Microsoft for 18 years building high-scale, secure platforms.
          Emma is an author with a PhD in microbiology. She wrote her first book while commuting on the bus, waiting for
          her professor to finish reviewing her dissertation.
          So she's still writing papers, but they are a lot more fun than her more academic path would have taken her.
        </p>
      </div>
    </div>
  );
}

export function TermsPage() {
  useEffect(() => { document.title = 'Terms of Service · Shelfkin'; }, []);
  return (
    <div className="static-page">
      <div className="static-page-header">
        <h1 className="static-page-title">Terms of Service</h1>
      </div>
      {/* TODO: confirm effective date before publishing */}
      <p className="static-page-meta">Effective date: 2026-10-27 · Applies to the Shelfkin website</p>
      <div className="static-page-body">

        <p className="static-page-intro">
          These Terms of Service govern your access to and use of Shelfkin, a tool for authors to make
          video ads for their books, operated by Kinglet Books. By creating an account or using the site,
          you agree to these terms. Please read them carefully.
        </p>

        <nav className="static-toc">
          <div className="static-toc-title">Contents</div>
          <ol>
            <li><a href="#eligibility">Eligibility</a></li>
            <li><a href="#accounts">Accounts</a></li>
            <li><a href="#content">Your Content</a></li>
            <li><a href="#ai">AI Features</a></li>
            <li><a href="#acceptable-use">Acceptable Use</a></li>
            <li><a href="#limits">Usage Limits &amp; Fees</a></li>
            <li><a href="#intellectual-property">Intellectual Property</a></li>
            <li><a href="#disclaimers">Disclaimers</a></li>
            <li><a href="#termination">Termination</a></li>
            <li><a href="#changes">Changes to These Terms</a></li>
            <li><a href="#contact">Contact</a></li>
          </ol>
        </nav>

        <section id="eligibility">
          <h2>1 · Eligibility</h2>
          <p>You must be at least <strong>18 years old</strong>, or the age of legal majority in your jurisdiction, to use this site. By using the Shelfkin website, you represent that you meet this requirement. If we become aware that an account belongs to someone under this age, we will close it.</p>
          <p>These Terms are governed by the laws of British Columbia, Canada. Any disputes arising from your use of the site are subject to the exclusive jurisdiction of the courts of British Columbia.</p>
        </section>

        <section id="accounts">
          <h2>2 · Accounts</h2>
          <p>You may create one account per person. You are responsible for maintaining the security of your account and for all activity that occurs under it. Do not share your sign-in access with others.</p>
          <p>New accounts are reviewed by hand before they can use the site, and we may approve, decline, or later suspend any account at our discretion. You agree to provide accurate information when registering and to keep it up to date.</p>
        </section>

        <section id="content">
          <h2>3 · Your Content</h2>
          <p>You retain ownership of the images, music, video, text, book details, and other material you upload or write on Shelfkin, and of the videos you render from them ("Your Content"). Your Content belongs to your account alone: it is never shown to, shared with, or used by other Shelfkin users.</p>
          <p>By uploading Your Content, you grant us a non-exclusive, royalty-free licence to store, copy, process, and transform it solely to provide the service to you — for example, to make thumbnails, measure images, generate descriptions and drafts, and render your videos. This licence ends when you delete Your Content or close your account, except for copies we must keep as described in our <a href="/privacy">Privacy Policy</a>.</p>
          <p>You represent that you own or have the necessary rights to Your Content — including licences for any music, artwork, fonts, or cover images — for both uploading it here and using it in the ads you make, and that it does not infringe any third party's rights.</p>
        </section>

        <section id="ai">
          <h2>4 · AI Features</h2>
          <p>Some features use an AI model to describe your images and to draft video text and layouts. AI features are off for each account until we turn them on. When they are on, the content needed for that task (such as an image, its book details, and your hooks) is sent to a 3rd party for processing — see our <a href="/privacy">Privacy Policy</a>.</p>
          <p>AI output can be inaccurate, unsuitable, or similar to output produced for others. Nothing Shelfkin drafts is published automatically: <strong>you are responsible for reviewing and editing everything before you use it</strong>, and for making sure your ads comply with the policies of the platforms where you run them and with applicable advertising law.</p>
        </section>

        <section id="acceptable-use">
          <h2>5 · Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Upload content that is unlawful, infringing, sexually explicit involving minors, hateful, or that you do not have the rights to use</li>
            <li>Use Shelfkin to make ads that are deceptive or that impersonate another author, publisher, or brand</li>
            <li>Exploit bugs, circumvent usage limits, or use automated tools or scripts to access the site</li>
            <li>Attempt to access another user's account, files, or renders</li>
            <li>Use the site to store files unrelated to making ads for your books</li>
          </ul>
          <p>We reserve the right to remove content and to suspend or ban any account at our sole discretion — particularly, but not only, where we believe these Terms, or their spirit, have been violated.</p>
        </section>

        <section id="limits">
          <h2>6 · Usage Limits &amp; Fees</h2>
          <p>Each account has limits on storage, renders, and AI use, which we set and may change. Renders are queued and processed in order; we do not guarantee how quickly a render will complete.</p>
        </section>

        <section id="intellectual-property">
          <h2>7 · Intellectual Property</h2>
          <p>All content on this site — including but not limited to site design, text, code, templates, and built-in images — is owned by or licensed to Kinglet Books, aside from Your Content. You may use built-in templates and images in the ads you make with Shelfkin. Otherwise, you may not copy, reproduce, scrape, or redistribute any part of the site without our prior written permission.</p>
          <p>Nothing in these Terms transfers any intellectual property rights to you beyond the licence needed to use the site.</p>
        </section>

        <section id="disclaimers">
          <h2>8 · Disclaimers &amp; Limitations</h2>
          <p>The site is provided <strong>"as is"</strong> without warranties of any kind. We do not guarantee that the site will be available at any particular time, error-free, or free of harmful components. We do not guarantee any particular ad performance, sales, or results. <strong>Keep your own copies of your files and finished videos</strong>; Shelfkin is not a backup service.</p>
          <p>To the fullest extent permitted by applicable law, Kinglet Books will not be liable for any indirect, incidental, or consequential damages arising from your use of the site, including loss of data.</p>
          <p>Nothing in these Terms limits liability that cannot be excluded under British Columbia law.</p>
        </section>

        <section id="termination">
          <h2>9 · Termination</h2>
          <p>We may suspend or terminate your account at any time if we believe you have violated these Terms or if we determine that doing so is necessary to protect the site or its users. We may also discontinue Shelfkin; if we do, we will give you reasonable notice to download your files and videos.</p>
          <p>You may delete individual files at any time, and you may close your account by contacting us at the address below. On closure, your files, projects, and renders will be deleted. We may retain certain data as described in our <a href="/privacy">Privacy Policy</a>.</p>
        </section>

        <section id="changes">
          <h2>10 · Changes to These Terms</h2>
          <p>We may update these Terms from time to time. When we make material changes, we will notify registered users by email or by a notice on the site. Continued use of the site after notice of changes constitutes your acceptance of the updated Terms.</p>
        </section>

        <section id="contact">
          <h2>11 · Contact</h2>
          <p>Questions or concerns about these Terms can be directed to:</p>
          <div className="static-contact">
            <strong>Shelfkin</strong>
            <a href="mailto:contact@kingletbooks.com">contact@kingletbooks.com</a>
          </div>
        </section>

      </div>
    </div>
  );
}

export function PrivacyPage() {
  useEffect(() => { document.title = 'Privacy Policy · Shelfkin'; }, []);
  return (
    <div className="static-page">
      <div className="static-page-header">
        <h1 className="static-page-title">Privacy Policy</h1>
      </div>
      <p className="static-page-meta">Effective date: 2026-10-27 · Applies to the Shelfkin website</p>
      <div className="static-page-body">

        <p className="static-page-intro">
          At Kinglet Books, we are committed to providing exceptional service while protecting the
          personal information of our users. This Privacy Policy explains what information Shelfkin
          collects, how we use it, who we share it with, and what rights you have. We comply with
          British Columbia's <em>Personal Information Protection Act</em> (PIPA) and, for users in the
          European Union and United Kingdom, with the General Data Protection Regulation (GDPR).
        </p>

        <nav className="static-toc">
          <div className="static-toc-title">Contents</div>
          <ol>
            <li><a href="#collect">Personal Data We Collect</a></li>
            <li><a href="#use">How We Use Your Data</a></li>
            <li><a href="#consent">Consent</a></li>
            <li><a href="#legal-basis">Legal Basis for Processing (GDPR)</a></li>
            <li><a href="#sharing">Who We Share Data With</a></li>
            <li><a href="#retention">How Long We Retain Data</a></li>
            <li><a href="#accuracy">Accuracy &amp; Correction</a></li>
            <li><a href="#security">How We Protect Your Data</a></li>
            <li><a href="#international">International Data Transfers</a></li>
            <li><a href="#rights">Your Rights</a></li>
            <li><a href="#children">Children</a></li>
            <li><a href="#cookies">Cookie Policy</a></li>
            <li><a href="#dnt">Do Not Track</a></li>
            <li><a href="#contact">Questions &amp; Privacy Officer</a></li>
          </ol>
        </nav>

        <section id="collect">
          <h2>1 · Personal Data We Collect</h2>
          <p>We only collect data that is necessary for the purposes described in this Policy. We will not collect additional data without notifying you first.</p>
          <h3>Data you provide</h3>
          <p>When you create an account or use the Shelfkin website, you may provide:</p>
          <ul>
            <li>Email address (used to sign you in, either with a one-time code or through your Google account)</li>
            <li>Book titles and series names</li>
            <li>Files you upload — images, music, and video — and the tags, descriptions, and other details you add to them</li>
            <li>Video projects, hooks, slide text, and reusable snippets you write or edit</li>
          </ul>
          <h3>Data we generate</h3>
          <ul>
            <li>Descriptions, moods, and suggested roles for your images (written by AI when AI features are on for your account)</li>
            <li>Measurements of your images, such as their colours and where text fits</li>
            <li>The videos and captions rendered from your projects</li>
            <li>A record of your render and AI jobs, used to apply usage limits</li>
          </ul>
          <h3>Data collected automatically</h3>
          <p>When you use the Site, we may automatically collect:</p>
          <ul>
            <li><strong>Request logs</strong> — IP address, browser type, the page or feature requested, and the time of the request</li>
            <li><strong>Diagnostic logs</strong> — errors, timings, and the actions you take (such as uploading a file or starting a render), linked to your account's internal ID so we can trace a problem you report</li>
          </ul>
          <p>These logs are used only to operate the Site, debug problems, and prevent abuse. They do not include the contents of your files, and they are not used for advertising or to build a profile of you.</p>
        </section>

        <section id="use">
          <h2>2 · How We Use Your Data</h2>
          <p>Data collected on our Site will only be used for the purposes specified in this Policy. We will not use your data beyond what is disclosed here. Specifically, we collect and use personal information in order to:</p>
          <ul>
            <li>Open and manage your account, and review new sign-ups</li>
            <li>Verify your identity when you sign in</li>
            <li>Store your files and projects, and show them back to you</li>
            <li>Describe your images and draft videos, when AI features are on for your account</li>
            <li>Render your final videos</li>
            <li>Apply storage and usage limits</li>
            <li>Maintain a high standard of service</li>
            <li>Meet regulatory requirements</li>
            <li>Monitor website performance, debug issues, and prevent fraud</li>
          </ul>
          <p>We do not use your files to train AI models, and we do not use them for our own marketing.</p>
        </section>

        <section id="consent">
          <h2>3 · Consent</h2>
          <p>By using the Site, you consent to the collection, use, and disclosure of your personal information as described in this Policy.</p>
          <p>Consent may be provided explicitly or implied where the purpose for collection is obvious and you voluntarily provide your information.</p>
          <p>You may withdraw consent at any time, subject to legal or contractual restrictions. Withdrawing consent for certain uses may limit our ability to provide particular services — we will explain this if it applies. Please contact our Privacy Officer to exercise your right to withdraw consent.</p>
          <h3>When we may act without consent</h3>
          <p>In limited circumstances, we may collect, use, or disclose your personal information without your knowledge or consent, including when:</p>
          <ul>
            <li>Required or permitted by law</li>
            <li>Necessary to obtain legal advice</li>
            <li>Required to collect a debt or investigate a breach of an agreement</li>
            <li>Necessary to protect ourselves from fraud</li>
          </ul>
        </section>

        <section id="legal-basis">
          <h2>4 · Legal Basis for Processing (GDPR)</h2>
          <div className="static-callout">
            <p>This section applies to users in the <strong>European Union</strong> and <strong>United Kingdom</strong>. We adhere to Regulation (EU) 2016/679 (the GDPR) and, for UK users, the GDPR as enshrined in the Data Protection Act 2018.</p>
          </div>
          <p>We collect and process personal data about EU/UK users only when we have a lawful basis under Article 6 of the GDPR. Our primary basis is <strong>contractual necessity</strong>: processing is required to take steps at your request before entering a contract, or to perform a contract to which you are a party. If you do not provide the necessary personal data, you will be unable to use the website.</p>
          <p>We have not appointed a Data Protection Officer, as we do not fall within the categories of controllers and processors required to do so under Article 37 of the GDPR.</p>
        </section>

        <section id="sharing">
          <h2>5 · Who We Share Data With</h2>
          <h3>Other Shelfkin users</h3>
          <p>None. Your account details, files, projects, and videos are private to your account and are never visible to other Shelfkin users.</p>
          <h3>Within our organisation</h3>
          <p>We may disclose your data to members of our organisation who reasonably need access to it to carry out the purposes described in this Policy — for example, to review sign-ups, set usage limits, or fix a failed render. Access to personal information is limited to a small number of administrators.</p>
          <h3>Service providers</h3>
          <p>We use a small number of service providers, each only for the purpose listed:</p>
          <ul>
            <li><strong>Anthropic</strong> — when AI features are on for your account, receives the images and text needed to describe your files and draft your videos.</li>
            <li><strong>Google (Firebase Authentication)</strong> — only if you choose to sign in with Google; it confirms your email address to us</li>
          </ul>
          <h3>Other disclosures</h3>
          <p>We will not sell or share your data with other parties, except in the following cases:</p>
          <ul>
            <li>If required by law or legal proceedings</li>
            <li>To prove or protect our legal rights</li>
            <li>To buyers or potential buyers of this company in the event we seek to sell it</li>
          </ul>
          <p>If you follow links from our Site to another website, we are not responsible for and have no control over the privacy policies of those third-party sites.</p>
        </section>

        <section id="retention">
          <h2>6 · How Long We Retain Data</h2>
          <p>We retain your personal information only as long as necessary to fulfill the purposes for which it was collected, or as required for legal or legitimate business purposes.</p>
          <p>Files you delete are removed from storage. When you close your account, we delete your files, projects, and videos; we may keep your email address and account status for a limited time to prevent abuse and to answer any questions about the account.</p>
          <p>If we use your personal information to make a decision that directly affects you, we will retain that information for at least one year to give you a reasonable opportunity to request access to it.</p>
          <p>You will be notified if your data is kept for longer than its original purpose requires.</p>
        </section>

        <section id="accuracy">
          <h2>7 · Accuracy &amp; Correction</h2>
          <p>We make reasonable efforts to ensure that your personal information is accurate and complete, particularly where it may be used to make decisions about you or shared with another organisation.</p>
          <p>You may request a correction to your personal information at any time. Requests must be made in writing and include sufficient detail to identify the information and the correction being sought. Please contact our Privacy Officer.</p>
          <p>If the information is found to be inaccurate or incomplete, we will correct it and send the corrected information to any organisation to which we disclosed the information within the previous year. If a correction is not made, we will note your correction request in the file.</p>
        </section>

        <section id="security">
          <h2>8 · How We Protect Your Data</h2>
          <p>We are committed to protecting your personal information from unauthorised access, collection, use, disclosure, copying, modification, or disposal. Our security measures include:</p>
          <ul>
            <li><strong>No passwords stored</strong> — we use one-time codes (OTP) or Google sign-in, so we never need to request or retain passwords or similar secrets.</li>
            <li><strong>Private storage</strong> — your files and videos are kept in private storage and are only served to your own signed-in account.</li>
            <li><strong>Limited administrator access</strong> — access to personal information is restricted to a small number of administrators.</li>
            <li><strong>User control</strong> — you can edit or delete your files, projects, and snippets at any time.</li>
          </ul>
          <p>We continually review and update our security policies as technology evolves. While we take all reasonable precautions, the Internet as a whole can be insecure and we cannot guarantee absolute security beyond what is reasonably practical.</p>
        </section>

        <section id="international">
          <h2>9 · International Data Transfers</h2>
          <p>We transfer user personal data to the <strong>United States</strong>, where our hosting and AI providers process it. When we do so, we protect that data as described in this Policy and comply with applicable legal requirements for international transfers.</p>
          <div className="static-callout">
            <p>If you are located in the <strong>EU or UK</strong>, we will only transfer your personal data if the destination country has been deemed to have adequate data protection by the European Commission (or by UK adequacy regulations), or if we have implemented appropriate safeguards — for example, EU or UK standard data protection contractual clauses with the recipient.</p>
          </div>
        </section>

        <section id="rights">
          <h2>10 · Your Rights</h2>
          <h3>Rights under PIPA (all users)</h3>
          <p>You have the right to request access to your personal information held by us, and to know how it has been used and to whom it has been disclosed. Requests must be made in writing with sufficient detail to identify the information sought.</p>
          <p>We will respond within 30 business days, or provide written notice if additional time is required. A minimal fee may apply; we will inform you of any cost before proceeding. If a request is refused in full or in part, we will notify you in writing, explaining the reasons and available recourse.</p>
          <h3>Additional rights under the GDPR (EU &amp; UK users)</h3>
          <p>If you are located in the EU or UK, you also have the right to:</p>
          <ul>
            <li><strong>Be informed</strong> about how your data is used</li>
            <li><strong>Access</strong> your personal data</li>
            <li><strong>Rectification</strong> of inaccurate data</li>
            <li><strong>Erasure</strong> ("right to be forgotten")</li>
            <li><strong>Restrict processing</strong> of your data</li>
            <li><strong>Data portability</strong></li>
            <li><strong>Object</strong> to certain processing activities</li>
          </ul>
          <h3>How to exercise your rights</h3>
          <p>To exercise any of these rights — including accessing, modifying, or deleting your data — please contact our Privacy Officer at the address below. Unresolved concerns may also be directed to the <strong>Information and Privacy Commissioner of British Columbia</strong>.</p>
        </section>

        <section id="children">
          <h2>11 · Children</h2>
          <p>Shelfkin is a tool for authors and is not directed to children. We do not knowingly collect or use personal data from children under the age of 18. If we learn that we have collected personal data from a child under 18, it will be deleted as soon as possible.</p>
        </section>

        <section id="cookies">
          <h2>12 · Cookie Policy</h2>
          <p>A cookie is a small file stored on your device by a website, used to collect data relating to your browsing activity. You can choose to be notified each time a cookie is set, or disable cookies entirely in your browser — though doing so may affect the quality of your experience on the Site, including your ability to stay signed in.</p>
          <p>We use only one type of cookie:</p>
          <ul>
            <li><strong>Essential cookies</strong> — a single sign-in cookie used to keep you authenticated. This cookie is required for the Site to function and cannot be disabled.</li>
          </ul>
          <p>We do not use functional, targeting, marketing, or third-party advertising cookies. If you sign in with Google, Google's sign-in window is subject to Google's own privacy policy.</p>
        </section>

        <section id="dnt">
          <h2>13 · Do Not Track</h2>
          <p>Do Not Track (DNT) is a privacy preference you can set in certain web browsers. We do not track users across third-party websites over time, and therefore do not respond to browser-initiated DNT signals. We are not responsible for how third parties that interact with our Site respond to DNT signals.</p>
        </section>

        <section id="contact">
          <h2>14 · Questions &amp; Privacy Officer</h2>
          <p>Our Privacy Officer is responsible for ensuring Kinglet Books's compliance with this Policy and the <em>Personal Information Protection Act</em>. Please direct any complaints, concerns, or access requests in writing to:</p>
          <div className="static-contact">
            <strong>Privacy Officer — Kinglet Books</strong>
            <a href="mailto:contact@kingletbooks.com">contact@kingletbooks.com</a>
          </div>
          <p style={{ marginTop: '1.25rem' }}>If our Privacy Officer is unable to resolve your concern, you may also contact the <strong>Information and Privacy Commissioner of British Columbia</strong>.</p>
          <h3>Changes to this Policy</h3>
          <p>This Privacy Policy may be amended from time to time. We will notify users of any material changes. Continued use of the Site following notice of changes constitutes acceptance of the updated Policy.</p>
        </section>

      </div>
    </div>
  );
}

